import React, { useRef, useMemo, useEffect, useState } from 'react';
import clsx from 'clsx';
import { findIndex } from 'lodash';
import Grid from '@mui/material/Grid';
import { displayDateText } from '@util/moment';
import { Button, Stack, TextField, Alert } from "@curves/ui-base";
import useMemberContactRecordApi from '@apis/useMemberContactRecordApi';
import useContactRecordApi from '@apis/useContactRecordApi';
import { EditableInfo } from "@icoach/components";

const EditContactRecordCard = React.forwardRef((props, ref) => {
        const {
            contactID,
            targetID,  //會員或顧客代碼
            peopleType,  //會員類型或顧客類型
            onConfirm,
            onClose
        } = props;

        const [contactRecords, setContactRecords] = useState([]);
        const [tabIndex, setTabIndex] = useState(0);
        const { putMemberContactRecordApi, getMembersContactRecordsLast5DataApi } = useMemberContactRecordApi();
        const { putCustomerContactRecordDataApi, getCustomerContactRecordLast5DataApi } = useContactRecordApi();
        const titleRef = useRef(null);
        const descriptionRef = useRef(null);

        const contactApiMap = {
            "1": {
                getApi: getCustomerContactRecordLast5DataApi,
                putApi: putCustomerContactRecordDataApi,
            },
            "2": {
                getApi: getMembersContactRecordsLast5DataApi,
                putApi: putMemberContactRecordApi,
            }
        }

        const disabled = !(contactRecords && contactRecords.length > 0
            && contactRecords[tabIndex].contactID > 0 && tabIndex === 0);

        const getAllParams = () => {
            const title = titleRef.current.getResult();
            const notes = descriptionRef.current.getResult();
            return {
                title,
                notes,
            };
        };

        const getTextNote = (text_1, text_2) => {
            let textAry = [];
            if (text_1) textAry.push(text_1);
            if (textAry.length !== 0 && text_2) textAry.push('~');
            if (text_2) textAry.push(text_2);
            if (textAry.length === 0) return '　';
            return textAry.join('');
        };

        const getContactRecordApi = async (targetID) => {
            const resp = await contactApiMap[peopleType].getApi(targetID);
            if (resp) {
                setContactRecords(resp);
                const index = findIndex(resp, { 'contactID': contactID });
                setTabIndex(index >= 0 ? index : 0);
            }
        }

        const putContactRecordApi = async (targetID, contactID, params) => {
            return await contactApiMap[peopleType].putApi(targetID, contactID, params);
        };

        const handleSelectRecord = (index) => {
            if (index !== tabIndex) {
                setTabIndex(index);
            }
        }

        const handleConfirm = async () => {
            const params = getAllParams();
            const resp = await putContactRecordApi(targetID, contactID, params);
            if (resp) {
                typeof onConfirm === 'function' && onConfirm(targetID, params);
                typeof onClose === 'function' && onClose();
            }
        };

        const contactRecordTabs = useMemo(() => {
                return <ul className="contact-record-tab">
                    {contactRecords.map((item, i) => (
                        <li key={`${targetID}-${i}`} className={clsx('contact-record-tab-item', { 'active': i === tabIndex })}
                            onClick={() => handleSelectRecord(i)}>
                            <div className="contact-record-tab-title" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                <span>{displayDateText(item.contactDate)}</span>
                                &nbsp;
                                {item.contactID === 0
                                    ? <span>簡訊</span>
                                    : !!item.contactResultType && <span>{item.contactResultTypeText}</span>
                                }
                                <span>{!!item.employeeDisplayName && ` - ${item.employeeDisplayName}`}</span>
                            </div>
                            <div className="contact-record-tab-result font-color-3" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {getTextNote(item.title, item.notes)}
                            </div>

                        </li>
                    ))}
                </ul>;

            },
            // eslint-disable-next-line
            [contactRecords, tabIndex]
        );

        useEffect(
            () => {
                getContactRecordApi(targetID);
            },
            // eslint-disable-next-line
            [targetID],
        );

        return (
            <Grid spacing={2} container>
                <Grid xs={5} item className="contact-record-selector">
                    {contactRecordTabs}
                </Grid>
                <Grid xs={7} item>
                    {contactRecords.length > 0 && contactRecords[tabIndex] &&
                        <Grid spacing={2} container>
                            <Grid xs={12} item>
                                <EditableInfo label={'聯繫日期'}>{displayDateText(contactRecords[tabIndex].contactDate)}</EditableInfo>
                            </Grid>
                            <Grid xs={6} item>
                                <EditableInfo label={'聯繫狀態'}>
                                    {contactRecords[tabIndex].contactID === 0
                                        ? '簡訊'
                                        : (!!contactRecords[tabIndex].contactResultType ? contactRecords[tabIndex].contactResultTypeText : '-')}
                                </EditableInfo>
                            </Grid>
                            <Grid xs={6} item>
                                <EditableInfo label={'聯繫擔當'}>{contactRecords[tabIndex].employeeDisplayName}</EditableInfo>
                            </Grid>
                            <Grid xs={12} item>
                                <TextField key={tabIndex} ref={titleRef} label="標題"
                                           defaultValue={contactRecords[tabIndex].title} fullWidth disabled={disabled} />
                            </Grid>
                            <Grid xs={12} item>
                                <TextField key={tabIndex} ref={descriptionRef} label="聯繫備註"
                                           defaultValue={contactRecords[tabIndex].notes} rows={5} multiline fullWidth disabled={disabled} />
                            </Grid>
                            <Grid item xs={12} className="h-8rem">
                                {contactRecords[tabIndex].contactID > 0 && tabIndex === 0
                                    ? <Stack justifyContent="end">
                                        <Button type="submit" variant="contained" color="secondary" onClick={handleConfirm}>
                                            更新
                                        </Button>
                                    </Stack>
                                    : <Alert severity="info" variant="filled" color="info" className="alert-justify-content-start font-size-small">
                                        非最近一筆聯繫紀錄或簡訊紀錄，將無法更新內容
                                    </Alert>
                                }
                            </Grid>
                        </Grid>}
                </Grid>
            </Grid>

        );
    })
;

export default EditContactRecordCard;
